import { Routes, Route, Navigate, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { api } from './lib/api'
import Layout from './components/Layout'
import PublicLayout, { FrameNote, PUBLIC_LINK, SignInFrame } from './components/PublicLayout'
import { MicrosoftSignInButton } from './components/carbon'
import Dashboard from './pages/Dashboard'
import ConfigurationDrift from './pages/ConfigurationDrift'
import UserInsights from './pages/UserInsights'
import Tenants from './pages/Tenants'
// TenantManagement is no longer routed — `/monitored-tenants` and
// `/tenant-management` now redirect to `/admin?tab=tenants` (Wave C-tab).
// UserManagement is no longer routed — `/user-management` now redirects to
// `/admin?tab=people` (Wave B-tab). OrganizationManagement is no longer
// routed either — `/organization-management` redirects to
// `/admin?tab=organizations` via LegacyOrgRedirect. All three files stay on
// disk until Wave E delete (per orchestration plan).
import AdminPortal from './pages/AdminPortal'
import AdminQueue from './pages/AdminQueue'
import AdminErrors from './pages/AdminErrors'
import AdminQaValidation from './pages/AdminQaValidation'
import SecurityCenter from './pages/SecurityCenter'
import Notifications from './pages/Notifications'
import ExecutiveReport from './pages/ExecutiveReport'
import Settings from './pages/Settings'
import Help from './pages/Help'
import TcmLabPage from './pages/TcmLabPage'
import AdminScans from './pages/AdminScans'
import DocsIndex from './pages/docs/DocsIndex'
import OnboardingGuide from './pages/docs/OnboardingGuide'
import PermissionsGuide from './pages/docs/PermissionsGuide'
import AdminConsentCallback from './pages/AdminConsentCallback'
import OnboardingStatus from './pages/OnboardingStatus'
import SetupWizard from './pages/SetupWizard'
import SignUp from './pages/signup/SignUp'
import SignUpCallback from './pages/signup/SignUpCallback'
import SignUpSuccess from './pages/signup/SignUpSuccess'
import { loginRequest } from './lib/auth'

// Guardian Admin Route Protection - uses API-based role check
function GuardianAdminRoute({ children }: { children: React.ReactNode }) {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    api.getCurrentUser()
      .then((userInfo) => {
        if (!cancelled) setIsAdmin(userInfo?.isGlobalAdmin === true)
      })
      .catch(() => {
        // Secure by default: if API fails, deny access
        if (!cancelled) setIsAdmin(false)
      })
    return () => { cancelled = true }
  }, [])

  // While loading, show nothing (prevents flash of protected content)
  if (isAdmin === null) return null

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

/**
 * TCM Lab Route Protection — Platform Admin only.
 *
 * Mirrors `GuardianAdminRoute` but checks `userInfo?.isPlatformAdmin`
 * instead of `isGlobalAdmin`. Platform Admin is a strictly narrower
 * cohort: iuvoTech-org users with the GlobalAdmin role (matches the
 * server-side `PlatformAdminHelper.IsPlatformAdminAsync` check the
 * `/tcm-lab` API endpoints already enforce). A Global Admin in any other
 * org is NOT a Platform Admin and must be bounced to the dashboard.
 *
 * `/me` is the only data dependency. We rely on the same fire-once
 * onboarding gate above to fetch fresh `/me` data — by the time this
 * guard mounts, the user's session is established and the API call here
 * is typically a cache hit.
 */
function TcmLabRoute({ children }: { children: React.ReactNode }) {
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    api.getCurrentUser()
      .then((userInfo) => {
        if (!cancelled) setIsPlatformAdmin(userInfo?.isPlatformAdmin === true)
      })
      .catch(() => {
        // Secure by default: deny on any /me failure (same posture as
        // GuardianAdminRoute — a stale or invalid session must not
        // expose the lab).
        if (!cancelled) setIsPlatformAdmin(false)
      })
    return () => { cancelled = true }
  }, [])

  if (isPlatformAdmin === null) return null

  if (!isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

/**
 * Platform Admin Route Protection — Platform Admin only.
 *
 * Identical gate to TcmLabRoute: checks `userInfo?.isPlatformAdmin` from
 * the `/me` endpoint. Factored out as a distinct component so `/admin/scans`
 * and future Platform-Admin-only pages share a named, self-documenting guard
 * rather than reusing a Lab-named component.
 */
function PlatformAdminRoute({ children }: { children: React.ReactNode }) {
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    api.getCurrentUser()
      .then((userInfo) => {
        if (!cancelled) setIsPlatformAdmin(userInfo?.isPlatformAdmin === true)
      })
      .catch(() => {
        if (!cancelled) setIsPlatformAdmin(false)
      })
    return () => { cancelled = true }
  }, [])

  if (isPlatformAdmin === null) return null

  if (!isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

/**
 * Legacy `/organization-management` URL → new `/admin?tab=organizations` URL.
 *
 * Preserves any `?orgId=...` deep-link by mapping it to the new drawer key
 * shape (`?drawer=org:<id>`). Without this redirect, every email, bookmark,
 * and cached browser history entry that points at the legacy page would
 * 404 once the portal ships. Kept as a thin shim until the legacy nav
 * entries are removed in Wave E.
 */
function LegacyOrgRedirect() {
  const [searchParams] = useSearchParams()
  const orgId = searchParams.get('orgId')
  const target = orgId
    ? `/admin?tab=organizations&drawer=org:${orgId}`
    : '/admin?tab=organizations'
  return <Navigate to={target} replace />
}

/**
 * Sign-in (mockup carbon/04, `04?state=default|loading`): the 04 frame with
 * Microsoft's own white button. While MSAL's redirect is in flight the
 * button shows its spinner and "Opening Microsoft sign-in". The "not linked"
 * and "pending" notifications live on /onboarding/status (D32).
 */
function LoginPage() {
  const { instance, inProgress } = useMsal()

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch(e => {
      console.error('Login failed:', e)
    })
  }

  return (
    <SignInFrame
      headline={<>Your Microsoft&nbsp;365 tenant, assessed by Guardian.</>}
      lede="Guardian checks your settings against your baseline and the frameworks you follow, then tells you what to close first."
    >
      <MicrosoftSignInButton onClick={handleLogin} busy={inProgress === InteractionStatus.Login} />
      <p className="m-0 mt-2 text-helper text-text-helper">Use your work account. Your password stays with Microsoft.</p>
      <FrameNote>
        <p className="m-0">
          New to Guardian?{' '}
          <Link to="/signup" className={PUBLIC_LINK}>
            Request access for your organization
          </Link>
        </p>
      </FrameNote>
    </SignInFrame>
  )
}

function App() {
  const isAuthenticated = useIsAuthenticated()
  const { instance, accounts } = useMsal()
  const location = useLocation()
  const navigate = useNavigate()

  // One-shot guard: the 403-on-getCurrentUser redirect must fire ONCE per
  // session, not once per render. Without this ref the effect would re-run
  // whenever the account list changes (token refresh, MSAL silent renewal,
  // etc.) and could trigger a redirect loop after the user has already been
  // bounced to /onboarding/status and then back. useState would force a
  // render; useRef keeps the guard out of the render path. After the hand-off
  // the OnboardingStatus page owns the retry/forward loop (Sub-piece C).
  const onboardingCheckRef = useRef<{ inFlight: boolean; done: boolean }>({
    inFlight: false,
    done: false,
  })

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      // Set the active account
      instance.setActiveAccount(accounts[0])
    }
  }, [isAuthenticated, accounts, instance])

  // 403-on-mount onboarding gate (Phase 5 of organizations-admin-ui §8).
  //
  // SCOPED TO A SINGLE getCurrentUser() CALL — NOT A GLOBAL INTERCEPTOR.
  // Tenant-scoped 403s from data hooks (usePriorityActions,
  // useComplianceData, etc.) must be handled locally by those hooks; a
  // global axios interceptor here would redirect away from legitimate
  // tenant-permission errors and lose the user's context.
  //
  // The fire-once ref also avoids redirecting after the user has already
  // been handed off to /onboarding/status: that page is rendered OUTSIDE
  // <AuthenticatedTemplate> (see the early-return above) and runs its own
  // getCurrentUser() poll loop, so this effect short-circuits on that path.
  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return
    if (location.pathname === '/onboarding/status') return
    if (onboardingCheckRef.current.done || onboardingCheckRef.current.inFlight) return

    onboardingCheckRef.current.inFlight = true
    api.getCurrentUser()
      .then(() => {
        onboardingCheckRef.current.done = true
        onboardingCheckRef.current.inFlight = false
      })
      .catch((err: unknown) => {
        onboardingCheckRef.current.inFlight = false
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          onboardingCheckRef.current.done = true
          // Hand off to the self-healing status page (Sub-piece C). It polls
          // getCurrentUser() and forwards into the app once the org-allowlist
          // gate opens (≤1 min after approval) — replacing the old sticky
          // /not-onboarded dead-end.
          navigate('/onboarding/status', { replace: true })
        }
        // 401 is already handled globally by the axios response interceptor
        // (forces re-login). 5xx and network errors are intentionally NOT
        // treated as "not onboarded" — those are transient and would falsely
        // bounce a legitimate user to the onboarding page.
      })
  }, [isAuthenticated, accounts, location.pathname, navigate])

  // Public routes bypass auth entirely - evaluated first
  if (location.pathname.startsWith('/docs')) {
    return (
      <Routes>
        <Route path="/docs" element={<PublicLayout />}>
          <Route index element={<DocsIndex />} />
          <Route path="onboarding" element={<OnboardingGuide />} />
          <Route path="permissions" element={<PermissionsGuide />} />
        </Route>
      </Routes>
    )
  }

  // Allow unauthenticated access to admin consent callback
  // This page is for external customer admins who have just granted consent
  // Security is handled via encrypted state parameter validation
  if (location.pathname === '/admin-consent-callback') {
    return (
      <Routes>
        <Route path="/admin-consent-callback" element={<AdminConsentCallback />} />
      </Routes>
    )
  }

  // Self-healing onboarding-status page (Sub-piece C), rendered standalone
  // outside <AuthenticatedTemplate>. Reached via the 403-on-getCurrentUser
  // redirect below (or directly). It must render standalone — no Layout
  // sidebar / breadcrumb / role-gated nav, none of which the user has access
  // to yet — and OUTSIDE the AuthenticatedTemplate -> Layout -> data-fetch
  // chain that would otherwise re-trigger the 403 in a loop. The page polls
  // getCurrentUser() itself and forwards into the app once the org-allowlist
  // gate opens. `/not-onboarded` is kept as a redirect alias for any existing
  // links or bookmarks.
  if (
    location.pathname === '/onboarding/status' ||
    location.pathname === '/not-onboarded'
  ) {
    return (
      <Routes>
        <Route path="/onboarding/status" element={<OnboardingStatus />} />
        <Route path="/not-onboarded" element={<Navigate to="/onboarding/status" replace />} />
      </Routes>
    )
  }

  // Allow unauthenticated access to self-service sign-up pages
  // These pages handle the public onboarding flow for new customers
  if (location.pathname.startsWith('/signup')) {
    return (
      <Routes>
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signup/callback" element={<SignUpCallback />} />
        <Route path="/signup/success" element={<SignUpSuccess />} />
      </Routes>
    )
  }

  // First-run setup wizard — authenticated but STANDALONE (no Layout
  // sidebar/breadcrumb): a freshly-approved customer has nothing else to
  // look at yet, and the wizard needs full focus. Layout auto-routes
  // eligible users here once per session (needsSelfServeSetup) and shows the
  // "finish setup" banner if they navigate away. An unapproved visitor who
  // lands here directly is still caught by the 403-on-getCurrentUser effect
  // above and handed to /onboarding/status.
  if (location.pathname === '/setup') {
    return (
      <>
        <UnauthenticatedTemplate>
          <LoginPage />
        </UnauthenticatedTemplate>
        <AuthenticatedTemplate>
          <Routes>
            <Route path="/setup" element={<SetupWizard />} />
          </Routes>
        </AuthenticatedTemplate>
      </>
    )
  }

  // All other routes go through existing MSAL auth pattern
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <Layout>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/configuration-drift" element={<ConfigurationDrift />} />
            <Route path="/drift" element={<Navigate to="/configuration-drift" replace />} />
            <Route path="/user-insights" element={<UserInsights />} />
            {/* Security Center - unified security posture hub */}
            <Route path="/security" element={<SecurityCenter />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/executive-report" element={<ExecutiveReport />} />
            {/* Legacy routes redirect to Security Center tabs for backward compatibility */}
            <Route path="/compliance" element={<Navigate to="/security?tab=secure-score" replace />} />
            <Route path="/compliance/standards" element={
              <Navigate to="/security?tab=best-practices" replace />
            } />
            {/* Legacy routes redirect to Baseline Compliance tabs */}
            <Route path="/baseline-reports" element={<Navigate to="/configuration-drift?tab=export" replace />} />
            <Route path="/tenants" element={<Tenants />} />
            {/* Legacy /monitored-tenants → /admin?tab=tenants. The legacy
                page accepted no query params (verified against
                TenantManagement.tsx), so a flat Navigate is sufficient — no
                LegacyTenantRedirect helper needed. The file stays on disk
                until Wave E delete. */}
            <Route path="/monitored-tenants" element={<Navigate to="/admin?tab=tenants" replace />} />
            {/* Legacy /tenant-management → /admin?tab=tenants. Same shape as
                the /monitored-tenants redirect (was previously a two-hop). */}
            <Route path="/tenant-management" element={<Navigate to="/admin?tab=tenants" replace />} />
            <Route path="/admin" element={
              <GuardianAdminRoute>
                <AdminPortal />
              </GuardianAdminRoute>
            } />
            {/* Legacy /organization-management → /admin?tab=organizations.
                Preserves ?orgId=... as ?drawer=org:... (Risk R4 in plan §6). */}
            <Route path="/organization-management" element={<LegacyOrgRedirect />} />
            {/* Legacy /user-management → /admin?tab=people. The legacy page
                accepted no query params (verified against UserManagement.tsx),
                so a flat Navigate is sufficient — no LegacyUserRedirect
                helper needed. The file stays on disk until Wave E delete
                so any in-flight references compile. */}
            <Route path="/user-management" element={<Navigate to="/admin?tab=people" replace />} />
            <Route path="/admin/queue" element={
              <GuardianAdminRoute>
                <AdminQueue />
              </GuardianAdminRoute>
            } />
            <Route path="/admin/errors" element={
              <GuardianAdminRoute>
                <AdminErrors />
              </GuardianAdminRoute>
            } />
            <Route path="/admin/qa-validation" element={
              <GuardianAdminRoute>
                <AdminQaValidation />
              </GuardianAdminRoute>
            } />
            {/* TCM Lab — Platform Admin only. Diagnostic surface for the
                Microsoft Tenant Configuration Management side-by-side
                pipeline. See .agents/Tasks/High Priority/tcm-sidebyside-blueprint.md §10. */}
            <Route path="/tcm-lab" element={
              <TcmLabRoute>
                <TcmLabPage />
              </TcmLabRoute>
            } />
            {/* Ad-Hoc Scans — Platform Admin only. Fleet-wide scan console
                replacing per-customer scan triggers removed in this wave. */}
            <Route path="/admin/scans" element={
              <PlatformAdminRoute>
                <AdminScans />
              </PlatformAdminRoute>
            } />
            {/* Legacy deep-link. Once pointed at the PR Tracker tab (retired
                with the TCM cutover); now lands on the Baseline Compliance
                default tab. */}
            <Route path="/config-as-code" element={<Navigate to="/configuration-drift" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/help" element={<Help />} />
            <Route path="/admin-consent-callback" element={<AdminConsentCallback />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Layout>
      </AuthenticatedTemplate>
    </>
  )
}

export default App